import { ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import InvestorInvitationResponseResource from "common/resources/InvestorInvitation/InvestorInvitationResponseResource";
import Button, { EButtonVariant } from "components/elements/Button";
import MessageBox from "components/elements/MessageBox";
import Modal from "components/elements/Modal";
import Typography, { ITypo, ITypoComponent } from "components/elements/Typography";
import I18n from "components/materials/I18n";

import classes from "./classes.module.scss";

type IProps = {
	result: { invitation: InvestorInvitationResponseResource; link: string; emailDelivery: boolean };
	/** `copied` : le conseiller recopie un lien existant, que le navigateur n'a pas pu copier seul. */
	origin: "created" | "resent" | "copied";
	onCopy: (link: string) => void;
	onClose: () => void;
};

const dateFormat = new Intl.DateTimeFormat("fr-FR", { dateStyle: "long" });

/**
 * Lien d'une invitation, à transmettre au client : à la création, au renvoi, ou quand le conseiller le recopie.
 * Wenimmo conserve le jeton chiffré tant que `invitation.isLinkAvailable` : le lien se recopie alors à tout moment.
 */
export default function InvitationLinkModal({ result, origin, onCopy, onClose }: IProps) {
	const copy = I18n.asset.pages.investor_invitations.link_modal;
	const { invitation, link, emailDelivery } = result;
	const title = origin === "created" ? copy.created_title : origin === "resent" ? copy.resent_title : copy.copy_title;
	// Après l'envoi du dossier, le lien sert au suivi et ne périme plus.
	const reminder = invitation.submittedAt
		? copy.kept_submitted
		: I18n.trslt(invitation.isLinkAvailable ? copy.kept : copy.shown_once, { date: dateFormat.format(invitation.expiresAt) });

	return (
		<Modal isOpen onClose={onClose} className={classes["modal"]}>
			<div className={classes["root"]}>
				<Typography typo={ITypo.H1} component={ITypoComponent.H2}>
					{title}
				</Typography>

				{origin !== "copied" &&
					(emailDelivery ? (
						<Typography typo={ITypo.P_MEDIUM}>{I18n.trslt(copy.email_sent, { email: invitation.email })}</Typography>
					) : (
						<MessageBox type="warning" text={copy.email_not_sent} />
					))}

				<div className={classes["link"]}>
					<label htmlFor="investor-invitation-link">
						<Typography typo={ITypo.P_MEDIUM}>{copy.link_label}</Typography>
					</label>
					<input id="investor-invitation-link" className={classes["link-input"]} value={link} readOnly onFocus={(event) => event.currentTarget.select()} />
					<Button icon={<ClipboardDocumentIcon />} iconposition="left" onClick={() => onCopy(link)}>
						{copy.copy}
					</Button>
				</div>

				<MessageBox type="info" text={reminder} />

				<Button variant={EButtonVariant.OUTLINED} onClick={onClose} className={classes["close"]}>
					{I18n.asset.common.close}
				</Button>
			</div>
		</Modal>
	);
}
